import React from "react";
import { Project, RenderConfig } from "types";
import { AxiosInstance } from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { FeedbackContext } from "context/Feedback";
import { FeedbackLocation } from "components/Feedback/FeedbackPopup";
import { DocType } from "components/Documents/types";
import { ControlContext } from "context/Controls";
import { useTranslation } from "react-i18next";

export interface GeneratePDFParams {
  selectedDocs: DocType[];
  selectedLanguages: string[];
  revisionName?: string;
  comment?: string;
  isUserGenerated?: boolean;
  isDryRun?: boolean;
  renderConfig?: RenderConfig;
  callback?: () => void;
}

const generatePDF = async (
  client: AxiosInstance,
  projectId: string,
  {
    selectedDocs,
    selectedLanguages,
    revisionName,
    comment,
    isUserGenerated,
    isDryRun,
    renderConfig,
  }: GeneratePDFParams
) =>
  client.post(`/projects/${projectId}/pdf`, {
    // filters: selectedDocs,
    filters: [], // do not set filters on documents. This breaks with the new document library (it'll only include documents that have been rendered before. Hence switiching a product category will not render new documents!)
    userGenerated: isUserGenerated,
    languages: selectedLanguages,
    dryRun: isDryRun,
    revisionName,
    comment,
    // Omitted entirely when the user changed nothing, which keeps the renderer
    // payload byte-identical to a render made before this feature existed.
    ...(renderConfig ? { renderConfig } : {}),
  });

export const useProjectDetails = (projectId: string) => {
  const client = useClient();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { displayFeedback } = React.useContext(FeedbackContext);
  const { displayNotification } = React.useContext(ControlContext);

  const { data: project, isLoading: loadingQuery } = useQuery<Project>(
    ["projects", projectId],
    async () => {
      const { data } = await client.get(`/projects/${projectId}`);

      return data;
    },
    {
      onError: (e) => {
        displayNotification!(
          "Kann Projektdaten nicht herunterladen. Die Seite bitte manuell neuladen.",
          // @ts-ignore
          (e as Error).response.data || (e as Error).message,
          "error",
          false
        );
      },
    }
  );

  const { mutate: updateVersion, isLoading: loadingMutation } = useMutation(
    async (params: GeneratePDFParams) =>
      await generatePDF(client, projectId, params),
    {
      onError: (e) => {
        displayNotification!(
          t("documents.download.generate_failure"),
          // @ts-ignore
          (e as Error).response.data || (e as Error).message,
          "error"
        );
      },
      onSuccess: async (_, { callback }) => {
        // Awaited so the generate dialog reopens against the render that just
        // happened. A repeated dry run replaces its version entry rather than
        // adding one, so a stale project here would silently offer the config
        // from the run before last.
        await queryClient.refetchQueries(["projects", projectId]);

        displayFeedback!(FeedbackLocation.Documents);
        displayNotification!(t("documents.download.generate_success"), "");

        callback && callback();
      },
    }
  );

  return {
    project,
    updateVersion,
    fetchInProgress: loadingQuery,
    updateInProgress: loadingMutation,
  };
};
